import { CocoChatModel } from '@cotera/client/app/v1/chat/models/coco-chat.model';
import {
  useStatefullyAwareInScopeModel,
  useWatchableValue,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import type { ApplicableContext } from '@cotera/client/v0/actions/framework';
import type { Conversation } from '@cotera/sdk/core';
import type { ChatSurface } from './coco-events';

/**
 * Returns the active Coco conversation ID from the nearest CocoChatModel in
 * scope, or null when not inside a Coco chat context.
 *
 * This is the single v0 → v1 bridge for analytics: v0 components import this
 * hook instead of CocoChatModel directly.
 */
export const useCocoConversationId = (): string | null => {
  const model = useStatefullyAwareInScopeModel(CocoChatModel);
  return model?.conversationId ?? null;
};

/**
 * Non-hook variant for use inside Action.track() methods.
 * Returns the conversationId from the targeted CocoChatModel, or null.
 */
export const getCocoConversationId = (
  context: ApplicableContext
): string | null => {
  const model = context.targetedModelOfType(CocoChatModel);
  return model?.conversationId ?? null;
};

/** Stable fallback so {@link useChatSurface} can call the hook unconditionally. */
const NULL_CONVERSATION_WATCHABLE = Watchable.fromValue<Conversation | null>(
  null
);

/**
 * Classify a loaded conversation's {@link ChatSurface}. Returns `null` when the
 * conversation hasn't loaded yet, so callers can decide how to treat
 * "not yet known" (e.g. defer firing an event until it resolves).
 *
 * Only `agent | workflow | scratch` are derivable here — `shared_link` and
 * `sub_agent` carry no signal on a normal chat mount (see {@link ChatSurface}).
 * Precedence: a focused agent or `agent` artifact → `agent`; else a `workflow`
 * artifact → `workflow`; else `scratch`.
 */
export const deriveChatSurface = (
  conversation: Conversation | null,
  seededSurface: ChatSurface | null = null
): ChatSurface | null => {
  // A chat seeded as agent/workflow keeps that surface even before the seed is
  // applied to the conversation. Agent/workflow nav uses `seedOnly`, so the
  // artifacts/focus_agent stay deferred until the first message and the loaded
  // conversation looks empty (scratch) until then — the seed is authoritative.
  if (seededSurface !== null) {
    return seededSurface;
  }
  if (conversation === null) {
    return null;
  }
  if (conversation.getFocusedAgentId() !== null) {
    return 'agent';
  }
  const artifactTypes = conversation.ownChatArtifacts().map((a) => a.type);
  if (artifactTypes.includes('agent')) {
    return 'agent';
  }
  if (artifactTypes.includes('workflow')) {
    return 'workflow';
  }
  return 'scratch';
};

/**
 * Non-hook variant for use inside `Action.track()` methods. Reads the targeted
 * CocoChatModel's conversation and classifies it, defaulting to `'scratch'`
 * when unavailable (actions fire from within a loaded chat).
 */
export const getChatSurface = (context: ApplicableContext): ChatSurface => {
  const model = context.targetedModelOfType(CocoChatModel);
  const conversation = model?.conversationWatchable?.snapshot() ?? null;
  return (
    deriveChatSurface(conversation, model?.seededSurface ?? null) ?? 'scratch'
  );
};

/**
 * Hook variant: reactively classify the nearest in-scope chat's surface.
 * Returns `null` until the conversation loads.
 */
export const useChatSurface = (): ChatSurface | null => {
  const model = useStatefullyAwareInScopeModel(CocoChatModel);
  const conversation = useWatchableValue(
    model?.conversationWatchable ?? NULL_CONVERSATION_WATCHABLE
  );
  return deriveChatSurface(conversation, model?.seededSurface ?? null);
};
